import { Inject, Injectable, NgZone, OnDestroy } from "@angular/core";
import { Subject } from "rxjs";
import { SessionCtl } from "../../services/session.ctl.service";
import { EnvMicroTreasury } from "../../config/env.micro.treasury";
import { TREASURY_ENV } from "../../config/micros-env.tokens";
import {
  CirculatingWalletLiveViewResponse,
  CollectorWalletLiveViewResponse,
  GeneralWalletLiveViewResponse,
  WalletLiveViewChannel,
  WalletLiveViewConnectionState,
  WalletRechargesLiveViewResponse,
} from "../../../domain/models/accounting/wallet-live-view.model";
import { WalletLiveViewPort } from "../../../app/ports/accounting/wallet-live-view.port";
import { WalletSseParser } from "../../parsers/wallet-sse.parser";
import {
  extractWalletLiveViewPayload,
  isCirculatingWalletLiveViewResponse,
  isCollectorWalletLiveViewResponse,
  isGeneralWalletLiveViewResponse,
  isWalletRechargesLiveViewResponse,
} from "../../validators/accounting/wallet-live-view.validator";

const CHANNELS: readonly WalletLiveViewChannel[] = [
  "collector",
  "wallet-recharges",
  "circulating",
  "general",
];

@Injectable()
export class WalletSseAdapter implements WalletLiveViewPort, OnDestroy {
  private readonly collectorSubject = new Subject<CollectorWalletLiveViewResponse>();
  private readonly walletRechargesSubject = new Subject<WalletRechargesLiveViewResponse>();
  private readonly circulatingSubject = new Subject<CirculatingWalletLiveViewResponse>();
  private readonly generalSubject = new Subject<GeneralWalletLiveViewResponse>();
  private readonly connectionStateSubject = new Subject<WalletLiveViewConnectionState>();
  private readonly controllers = new Map<WalletLiveViewChannel, AbortController>();
  private readonly reconnectTimers = new Map<WalletLiveViewChannel, ReturnType<typeof setTimeout>>();
  private readonly reconnectAttempts = new Map<WalletLiveViewChannel, number>();
  private companyCode: string | null = null;
  private activeChannels: readonly WalletLiveViewChannel[] = CHANNELS;
  private generation = 0;
  private intentionallyDisconnected = true;

  readonly collector$ = this.collectorSubject.asObservable();
  readonly walletRecharges$ = this.walletRechargesSubject.asObservable();
  readonly circulating$ = this.circulatingSubject.asObservable();
  readonly general$ = this.generalSubject.asObservable();
  readonly connectionState$ = this.connectionStateSubject.asObservable();

  constructor(
    @Inject(TREASURY_ENV) private readonly env: EnvMicroTreasury,
    private readonly sessionCtl: SessionCtl,
    private readonly zone: NgZone,
  ) {}

  connect(
    companyCode: string,
    channels: readonly WalletLiveViewChannel[] = CHANNELS,
  ): void {
    const normalizedCompanyCode = companyCode.trim().toUpperCase();
    const requestedChannels = CHANNELS.filter((channel) => channels.includes(channel));
    if (!this.env.enabled || !normalizedCompanyCode) return;
    if (!requestedChannels.length) return;
    if (
      !this.intentionallyDisconnected &&
      this.companyCode === normalizedCompanyCode &&
      requestedChannels.length === this.activeChannels.length &&
      requestedChannels.every((channel) => this.activeChannels.includes(channel))
    ) return;

    this.disconnect();
    this.intentionallyDisconnected = false;
    this.companyCode = normalizedCompanyCode;
    this.activeChannels = requestedChannels;
    const generation = this.generation;
    window.addEventListener("online", this.onNetworkOnline);
    this.activeChannels.forEach((channel) => this.openChannel(channel, generation));
  }

  disconnect(): void {
    this.intentionallyDisconnected = true;
    this.companyCode = null;
    this.generation += 1;
    window.removeEventListener("online", this.onNetworkOnline);
    this.controllers.forEach((controller) => controller.abort());
    this.controllers.clear();
    this.reconnectTimers.forEach((timer) => clearTimeout(timer));
    this.reconnectTimers.clear();
    this.reconnectAttempts.clear();
    CHANNELS.forEach((channel) => this.publishConnectionState(channel, "idle", 0));
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private async openChannel(
    channel: WalletLiveViewChannel,
    generation: number,
  ): Promise<void> {
    if (!this.canContinue(generation)) return;

    const reconnectAttempt = this.reconnectAttempts.get(channel) ?? 0;
    this.publishConnectionState(
      channel,
      reconnectAttempt === 0 ? "connecting" : "reconnecting",
      reconnectAttempt,
    );

    const controller = new AbortController();
    this.controllers.get(channel)?.abort();
    this.controllers.set(channel, controller);

    try {
      const response = await fetch(this.streamUrl(channel), {
        headers: this.requestHeaders(),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (!response.body) throw new Error("El stream SSE no contiene un body legible.");

      this.reconnectAttempts.set(channel, 0);
      this.publishConnectionState(channel, "connected", 0);
      await this.readChannel(channel, response.body, controller.signal, generation);

      if (!controller.signal.aborted && this.canContinue(generation)) {
        this.scheduleReconnect(channel, generation, "El stream SSE finalizó.");
      }
    } catch (error) {
      if (!controller.signal.aborted && this.canContinue(generation)) {
        this.scheduleReconnect(channel, generation, this.errorMessage(error));
      }
    } finally {
      if (this.controllers.get(channel) === controller) {
        this.controllers.delete(channel);
      }
    }
  }

  private async readChannel(
    channel: WalletLiveViewChannel,
    stream: ReadableStream<Uint8Array>,
    signal: AbortSignal,
    generation: number,
  ): Promise<void> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    const parser = new WalletSseParser();

    try {
      while (!signal.aborted && this.canContinue(generation)) {
        const { done, value } = await reader.read();
        if (done) break;
        const events = parser.push(decoder.decode(value, { stream: true }));
        for (const event of events) this.handleData(channel, event.data);
      }
    } finally {
      reader.releaseLock();
    }
  }

  private handleData(channel: WalletLiveViewChannel, rawData: string): void {
    let value: unknown;
    try {
      value = extractWalletLiveViewPayload(JSON.parse(rawData) as unknown);
    } catch {
      console.warn(`[WalletLiveView] Se ignoró JSON inválido en ${channel}.`);
      return;
    }

    this.zone.run(() => {
      if (channel === "collector" && isCollectorWalletLiveViewResponse(value)) {
        this.collectorSubject.next(value);
      } else if (channel === "wallet-recharges" && isWalletRechargesLiveViewResponse(value)) {
        this.walletRechargesSubject.next(value);
      } else if (channel === "circulating" && isCirculatingWalletLiveViewResponse(value)) {
        this.circulatingSubject.next(value);
      } else if (channel === "general" && isGeneralWalletLiveViewResponse(value)) {
        this.generalSubject.next(value);
      } else {
        console.warn(`[WalletLiveView] Se ignoró un payload inválido en ${channel}.`);
        return;
      }
      this.publishConnectionState(channel, "connected", 0, new Date().toISOString());
    });
  }

  private scheduleReconnect(
    channel: WalletLiveViewChannel,
    generation: number,
    error: string,
  ): void {
    const attempt = (this.reconnectAttempts.get(channel) ?? 0) + 1;
    this.reconnectAttempts.set(channel, attempt);
    this.publishConnectionState(channel, "reconnecting", attempt, null, error);
    const delay = Math.min(
      this.env.reconnectInitialDelayMs * 2 ** (attempt - 1),
      this.env.reconnectMaxDelayMs,
    );
    const timer = setTimeout(() => {
      this.reconnectTimers.delete(channel);
      void this.openChannel(channel, generation);
    }, delay);
    this.reconnectTimers.set(channel, timer);
  }

  private requestHeaders(): HeadersInit {
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (this.env.authMode === "bearer") {
      headers["Authorization"] = `Bearer ${this.sessionCtl.getAccessTokenOrThrow()}`;
    }
    return headers;
  }

  private streamUrl(channel: WalletLiveViewChannel): string {
    const baseUrl = `${this.env.host}${this.env.apiBasePath}`.replace(/\/$/, "");
    const stream = this.env.streams[channel];
    const path = stream.startsWith("/") ? stream : `/${stream}`;
    return `${baseUrl}${path}?companyCode=${encodeURIComponent(this.companyCode ?? "")}`;
  }

  private canContinue(generation: number): boolean {
    return !this.intentionallyDisconnected && this.generation === generation;
  }

  private publishConnectionState(
    channel: WalletLiveViewChannel,
    status: WalletLiveViewConnectionState["status"],
    reconnectAttempt: number,
    lastReceivedAt: string | null = null,
    error: string | null = null,
  ): void {
    this.zone.run(() => this.connectionStateSubject.next({
      channel,
      status,
      reconnectAttempt,
      lastReceivedAt,
      error,
    }));
  }

  private errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : "Error desconocido en el stream SSE.";
  }

  private readonly onNetworkOnline = (): void => {
    if (this.intentionallyDisconnected) return;
    const generation = this.generation;
    for (const channel of this.activeChannels) {
      if (this.controllers.has(channel)) continue;
      const timer = this.reconnectTimers.get(channel);
      if (timer) clearTimeout(timer);
      this.reconnectTimers.delete(channel);
      void this.openChannel(channel, generation);
    }
  };
}
